import type { NavigateFunction } from 'react-router';

import { bootstrapSession } from '@/lib/api';
import { queryClient } from '@/lib/query-client';
import {
  useChurchSwitchingStore,
  type ChurchSwitchingMode,
} from '@/stores/church-switching-store';
import { useChurchStore } from '@/stores/church-store';

/** Default console landing after choosing or switching a church. */
export const CONSOLE_HOME = '/home';

/** React Query roots kept when switching — auth/session probes and church pickers. */
const PRESERVED_QUERY_ROOTS = new Set([
  'session',
  'church-invites',
  'church-invite-preview',
  'my-churches',
]);

const shouldPreserveQuery = (queryKey: readonly unknown[]): boolean => {
  const root = queryKey[0];
  return typeof root === 'string' && PRESERVED_QUERY_ROOTS.has(root);
};

export const runChurchWorkspaceTransition = async (input: {
  action: () => Promise<void>;
  mode?: ChurchSwitchingMode;
  churchName?: string | null;
}): Promise<void> => {
  const { start, finish } = useChurchSwitchingStore.getState();
  start({ mode: input.mode, churchName: input.churchName });
  try {
    // Stop in-flight reads before the church header changes — retries would otherwise
    // hit the new tenant with URLs meant for the old one and amplify 401 refresh races.
    await queryClient.cancelQueries();
    await input.action();
    // Renew the access cookie before mounted views refetch — otherwise a burst of stale
    // 401s can race the single shared refresh and sign the person out.
    await bootstrapSession();
    queryClient.removeQueries({
      predicate: (query) => !shouldPreserveQuery(query.queryKey),
    });
    useChurchStore.getState().bumpWorkspaceGeneration();
  } finally {
    finish();
  }
};

export const switchChurchAndOpen = async (input: {
  action: () => Promise<void>;
  navigate: NavigateFunction;
  destination?: string;
  mode?: ChurchSwitchingMode;
  churchName?: string | null;
}): Promise<void> => {
  await runChurchWorkspaceTransition({
    action: input.action,
    mode: input.mode,
    churchName: input.churchName,
  });
  input.navigate(input.destination ?? CONSOLE_HOME, { replace: true });
};
